import React from "react";
import { Box, Stack, Container, Skeleton, useTheme, Grid, Paper, Divider } from "@mui/material";
import { keyframes } from "@emotion/react";

// Shimmer animation
const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

// Glass-style shimmer card - Horizontal layout
const StoryCardShimmer = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        borderRadius: 0,
        overflow: "hidden",
        backgroundColor: "transparent",
        backdropFilter: "none",
        border: "none",
        boxShadow: "none",
        position: "relative",
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          left: "-100%",
          width: "100%",
          height: "100%",
          background: isDark
            ? "linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)"
            : "linear-gradient(90deg, transparent, rgba(0,0,0,0.05), transparent)",
          animation: `${shimmer} 2s infinite`,
        },
      }}
    >
      {/* Badge skeleton - Top Right Corner */}
      <Skeleton
        variant="rectangular"
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: 3,
          width: 80,
          height: 24,
          borderRadius: 999,
          bgcolor: isDark
            ? "rgba(255,255,255,0.08)"
            : "rgba(0,0,0,0.08)",
        }}
      />

      {/* Image area - Left Side */}
      <Skeleton
        variant="rectangular"
        sx={{
          width: { xs: 140, sm: 160 },
          minWidth: { xs: 140, sm: 160 },
          aspectRatio: "3/4",
          height: { xs: "auto", sm: "auto" }, // Auto height to maintain 3:4 ratio
          borderRadius: 0,
          flexShrink: 0,
          bgcolor: isDark
            ? "rgba(255,255,255,0.05)"
            : "rgba(0,0,0,0.05)",
        }}
      />
      
      {/* Content area - Right Side */}
      <Stack spacing={1.5} sx={{ p: 2, flexGrow: 1, minWidth: 0 }}>
        {/* Title skeleton */}
        <Skeleton
          variant="text"
          width="85%"
          height={28}
          sx={{
            bgcolor: isDark
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.08)",
          }}
        />
        <Skeleton
          variant="text"
          width="60%"
          height={28}
          sx={{
            bgcolor: isDark
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.08)",
          }}
        />
        {/* Author skeleton */}
        <Skeleton
          variant="text"
          width="40%"
          height={18}
          sx={{
            bgcolor: isDark
              ? "rgba(255,255,255,0.06)"
              : "rgba(0,0,0,0.06)",
          }}
        />
        {/* Description skeleton */}
        <Skeleton
          variant="text"
          width="95%"
          height={16}
          sx={{
            bgcolor: isDark
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.08)",
          }}
        />
        <Skeleton
          variant="text"
          width="90%"
          height={16}
          sx={{
            bgcolor: isDark
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.08)",
          }}
        />
        <Skeleton
          variant="text"
          width="75%"
          height={16}
          sx={{
            bgcolor: isDark
              ? "rgba(255,255,255,0.08)"
              : "rgba(0,0,0,0.08)",
          }}
        />
        {/* Stats skeleton */}
        <Stack direction="row" spacing={2} sx={{ mt: "auto" }}>
          <Skeleton
            variant="rectangular"
            width={50}
            height={20}
            sx={{
              borderRadius: 1,
              bgcolor: isDark
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)",
            }}
          />
          <Skeleton
            variant="rectangular"
            width={50}
            height={20}
            sx={{
              borderRadius: 1,
              bgcolor: isDark
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)",
            }}
          />
          <Skeleton
            variant="rectangular"
            width={50}
            height={20}
            sx={{
              borderRadius: 1,
              bgcolor: isDark
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)",
            }}
          />
        </Stack>
        {/* Tags skeleton */}
        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1 }}>
          <Skeleton
            variant="rectangular"
            width={80}
            height={24}
            sx={{
              borderRadius: 1,
              bgcolor: isDark
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)",
            }}
          />
          <Skeleton
            variant="rectangular"
            width={70}
            height={24}
            sx={{
              borderRadius: 1,
              bgcolor: isDark
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)",
            }}
          />
          <Skeleton
            variant="rectangular"
            width={60}
            height={24}
            sx={{
              borderRadius: 1,
              bgcolor: isDark
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)",
            }}
          />
        </Stack>
      </Stack>
    </Box>
  );
};

// MyStoriesPage Shimmer
const MyStoriesPageShimmer = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const pageCount = 6;

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
      {/* Container Card Shimmer */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          backgroundColor: "transparent",
          backdropFilter: "blur(12px)",
          border: (theme) =>
            `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)"
            }`,
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 2px 8px rgba(0,0,0,0.1)"
              : "0 2px 8px rgba(0,0,0,0.05)",
          p: 3,
        }}
      >
        <Grid container spacing={0}>
          {Array.from({ length: pageCount }).map((_, index) => {
            const isFirstInRow = index % 2 === 0;
            const isLastInRow = (index + 1) % 2 === 0;
            const isInLastRow = index >= pageCount - (pageCount % 2 === 0 ? 2 : 1);

            return (
              <React.Fragment key={index}>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      p: 2,
                      height: "100%",
                      borderRight: (theme) =>
                        isFirstInRow && index < pageCount - 1
                          ? `1px solid ${
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.08)"
                                : "rgba(0,0,0,0.08)"
                            }`
                          : "none",
                      borderBottom: (theme) =>
                        !isInLastRow
                          ? `1px solid ${
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.08)"
                                : "rgba(0,0,0,0.08)"
                            }`
                          : "none",
                    }}
                  >
                    <StoryCardShimmer />
                  </Box>
                </Grid>
                {/* Mobile divider between rows */}
                {isLastInRow && !isInLastRow && (
                  <Grid item xs={12} sx={{ display: { xs: "block", sm: "none" } }}>
                    <Divider
                      sx={{
                        borderColor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.08)"
                            : "rgba(0,0,0,0.08)",
                      }}
                    />
                  </Grid>
                )}
              </React.Fragment>
            );
          })}
        </Grid>
      </Paper>

      {/* Pagination Shimmer */}
      <Stack direction="row" justifyContent="center" spacing={2} mt={6}>
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            width={40}
            height={40}
            sx={{
              borderRadius: 2,
              bgcolor: isDark
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)",
            }}
          />
        ))}
      </Stack>
    </Container>
  );
};

export default MyStoriesPageShimmer;
